import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { formatCurrency, formatDate } from '../../utils/formatters';
import TransactionModal from '../Transactions/TransactionModal';

// Agrupa transações parceladas (marcadas com installment:true ao lançar, ver
// TransactionModal) pelo recurrentGroupId — cada grupo é uma compra parcelada.
function groupInstallments(transactions) {
  const groups = {};
  transactions.filter(t => t.installment && t.recurrentGroupId).forEach(t => {
    if (!groups[t.recurrentGroupId]) groups[t.recurrentGroupId] = [];
    groups[t.recurrentGroupId].push(t);
  });
  return Object.values(groups).map(items => {
    const sorted = [...items].sort((a, b) => a.date.localeCompare(b.date));
    const baseDesc = sorted[0].description.replace(/\s*\(\d+\/\d+\)\s*$/, '');
    const total = sorted.reduce((s, t) => s + t.value, 0);
    const paidCount = sorted.filter(t => t.status === 'paid').length;
    const nextPending = sorted.find(t => t.status === 'pending');
    return { groupId: sorted[0].recurrentGroupId, description: baseDesc, items: sorted, total, paidCount, nextPending };
  }).sort((a, b) => (a.nextPending?.date || '9999') .localeCompare(b.nextPending?.date || '9999'));
}

export default function Installments() {
  const { state } = useApp();
  const [expanded, setExpanded] = useState(null);
  const [editingTx, setEditingTx] = useState(null);

  const groups = groupInstallments(state.transactions || []);
  const activeGroups = groups.filter(g => g.paidCount < g.items.length);
  const doneGroups = groups.filter(g => g.paidCount === g.items.length);
  const totalRemaining = activeGroups.reduce((s, g) => s + g.items.filter(i => i.status === 'pending').reduce((s2, i) => s2 + i.value, 0), 0);

  return (
    <div className="page-enter">
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 22, fontWeight: 700 }}>Parcelas</div>
        <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 2 }}>Acompanhe suas compras parceladas</div>
      </div>

      {activeGroups.length === 0 && doneGroups.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <i className="fas fa-layer-group"></i>
            <div>Nenhuma parcela encontrada</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6 }}>
              Parcelas aparecem aqui quando você lança uma compra em Lançamentos marcando "Valor digitado é o total — dividir em parcelas".
            </div>
          </div>
        </div>
      ) : (
        <>
          {activeGroups.length > 0 && (
            <div className="card" style={{ marginBottom: 12 }}>
              <div className="metric-label">Restante a pagar (parcelas em aberto)</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--red)' }}>{formatCurrency(totalRemaining)}</div>
            </div>
          )}

          {activeGroups.map(g => {
            const isExpanded = expanded === g.groupId;
            return (
              <div key={g.groupId} className="card" style={{ marginBottom: 10 }}>
                <div style={{ cursor: 'pointer' }} onClick={() => setExpanded(isExpanded ? null : g.groupId)}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>
                        <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'}`} style={{ fontSize: 10, marginRight: 8, color: 'var(--muted)' }}></i>
                        {g.description}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4, marginLeft: 18 }}>
                        {g.paidCount} de {g.items.length} pagas
                        {g.nextPending && ` · próxima em ${formatDate(g.nextPending.date + 'T12:00:00')}`}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{formatCurrency(g.total)}</div>
                      <div style={{ fontSize: 11, color: 'var(--muted)' }}>total</div>
                    </div>
                  </div>
                  <div className="progress-bar" style={{ height: 6, marginTop: 10, marginLeft: 18 }}>
                    <div className="progress-fill" style={{ width: `${(g.paidCount / g.items.length) * 100}%`, background: 'var(--gold)' }}></div>
                  </div>
                </div>
                {isExpanded && (
                  <div style={{ marginTop: 12, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {g.items.map(item => (
                      <div key={item.id} className="tx-item" style={{ padding: '8px 10px', cursor: 'pointer' }} onClick={() => setEditingTx(item)}>
                        <div className="tx-info">
                          <div className="tx-desc" style={{ fontSize: 12.5 }}>{item.description}</div>
                          <div className="tx-cat" style={{ fontSize: 11 }}>{formatDate(item.date + 'T12:00:00')}</div>
                        </div>
                        <div className="tx-right">
                          <div className={`tx-amount ${item.status === 'paid' ? 'text-green' : ''}`} style={{ fontSize: 12.5 }}>-{formatCurrency(item.value)}</div>
                          <span className={`tag ${item.status === 'paid' ? 'tag-green' : 'tag-amber'}`} style={{ fontSize: 10 }}>
                            {item.status === 'paid' ? 'Pago' : 'Pendente'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {doneGroups.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <div className="section-title" style={{ color: 'var(--muted)' }}>Quitadas ({doneGroups.length})</div>
              {doneGroups.map(g => (
                <div key={g.groupId} className="card" style={{ marginBottom: 8, opacity: 0.65 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}><i className="fas fa-circle-check" style={{ color: 'var(--green)', marginRight: 8 }}></i>{g.description}</div>
                    <div style={{ fontWeight: 700, fontSize: 13 }}>{formatCurrency(g.total)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {editingTx && <TransactionModal tx={editingTx} onClose={() => setEditingTx(null)} />}
    </div>
  );
}
